import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react'
import { Alert, Flex, Input } from 'antd'
import { Well } from '@pushly/aqe/lib/components'
import { PQueryBuilder } from '@pushly/aqe/lib/components/pquery-builder/pquery-builder'
import type { ValidationResult } from '@pushly/aqe/lib/components/pquery-builder'
import { PQueryBuilderProvider, VERTICAL_WELL_GAP } from '@pushly/aqe'
import type { PQueryBuilderField } from '@pushly/aqe/lib/components/pquery-builder'
import { useParams, useLocation, useHistory } from 'react-router-dom'
import { AppState } from '../../../stores/app'
import { AppService } from '../../../services'
import { ItemGroupsService } from '../../../services/item-groups.service'
import { CountPreview } from './count-preview'
import { ItemGroupFormSkeleton } from './item-group-form-skeleton'
import { ItemGroupResponse } from './types'
import { useService } from '@pushly/aqe/lib/hooks'
import { useActiveDomainChangeEffect } from '../../../hooks/use-active-domain-change-effect'
import { antdStaticApi } from 'src/providers/antd-app-provider/antd-static-api'
import { simpleNotification } from 'src/_utils/utils'

// ─── State Management ────────────────────────────────────────────────────────

interface FormState {
    name: string
    nameError: string | null
    submitting: boolean
    submitError: string | null
    dirty: boolean
    submitAttempted: boolean
    loading: boolean
    loadError: string | null
    builderValid: boolean
}

type FormAction =
    | { type: 'SET_NAME'; payload: string }
    | { type: 'SET_NAME_ERROR'; payload: string | null }
    | { type: 'SET_SUBMITTING'; payload: boolean }
    | { type: 'SET_SUBMIT_ERROR'; payload: string | null }
    | { type: 'SET_SUBMIT_ATTEMPTED' }
    | { type: 'UNSET_SUBMIT_ATTEMPTED' }
    | { type: 'SET_LOADING'; payload: boolean }
    | { type: 'SET_LOAD_ERROR'; payload: string | null }
    | { type: 'SET_BUILDER_VALID'; payload: boolean }
    | { type: 'POPULATE_FORM'; payload: { name: string } }

function formReducer(state: FormState, action: FormAction): FormState {
    switch (action.type) {
        case 'SET_NAME':
            return { ...state, name: action.payload, nameError: null, dirty: true }
        case 'SET_NAME_ERROR':
            return { ...state, nameError: action.payload }
        case 'SET_SUBMITTING':
            return { ...state, submitting: action.payload }
        case 'SET_SUBMIT_ERROR':
            return { ...state, submitError: action.payload }
        case 'SET_SUBMIT_ATTEMPTED':
            return { ...state, submitAttempted: true }
        case 'UNSET_SUBMIT_ATTEMPTED':
            return { ...state, submitAttempted: false }
        case 'SET_LOADING':
            return { ...state, loading: action.payload }
        case 'SET_LOAD_ERROR':
            return { ...state, loadError: action.payload }
        case 'SET_BUILDER_VALID':
            return { ...state, builderValid: action.payload }
        case 'POPULATE_FORM':
            return {
                ...state,
                name: action.payload.name,
                loading: false,
                dirty: false,
            }
        default:
            return state
    }
}

// ─── Constants ───────────────────────────────────────────────────────────────

const EMPTY_BUILDER_VALUE = { and: [] }

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Self-contained Item Group form. Owns all state, data fetching, validation,
 * and submission. Save/Cancel actions live in the Well footer.
 */
export const ItemGroupFormPage: React.FC = () => {
    const appState = useService(AppState)
    const appService = useService(AppService)
    const itemGroupsService = useService(ItemGroupsService)

    const { groupId } = useParams<{ groupId?: string }>()
    const location = useLocation<{ itemGroup?: ItemGroupResponse }>()
    const history = useHistory()
    const isEditMode = !!groupId

    // Extract item group from location.state (passed from list view) and discard
    const initialItemGroup = location.state?.itemGroup ?? null
    useEffect(() => {
        if (location.state?.itemGroup) {
            history.replace(location.pathname, {})
        }
    }, [])

    const domain = appState.currentDomain
    const timezone = domain?.timezone as string | undefined

    const [fields, setFields] = useState<PQueryBuilderField[] | null>(null)

    const [state, dispatch] = useReducer(formReducer, {
        name: '',
        nameError: null,
        submitting: false,
        submitError: null,
        dirty: false,
        submitAttempted: false,
        loading: isEditMode,
        loadError: null,
        builderValid: true,
    })

    // ─── Builder state in refs (avoids re-rendering PQueryBuilder on commit) ─
    const filtersJsonRef = useRef<Record<string, unknown> | null>(null)
    const builderValidRef = useRef(true)

    // Keep a ref to dirty state for the cancel handler
    const dirtyRef = useRef(state.dirty)
    useEffect(() => {
        dirtyRef.current = state.dirty
    }, [state.dirty])

    // Fetch field definitions on mount (uses singleton cache)
    useEffect(() => {
        if (!domain) return

        let cancelled = false

        const fetchFields = async () => {
            try {
                const fields = await itemGroupsService.getFieldsCached(domain.id)
                if (cancelled) return
                if (fields.length > 0) {
                    setFields(fields)
                } else {
                    simpleNotification('error', 'Unable to load field definitions.')
                }
            } catch {
                if (cancelled) return
                simpleNotification('error', 'Unable to load field definitions.')
            }
        }

        fetchFields()
        return () => {
            cancelled = true
        }
    }, [domain?.id, itemGroupsService])

    // Fetch existing item group data on mount (edit mode only, skipped if passed via location.state)
    useEffect(() => {
        if (!isEditMode || !domain || !groupId) return

        let cancelled = false

        const fetchGroup = async () => {
            dispatch({ type: 'SET_LOADING', payload: true })
            dispatch({ type: 'SET_LOAD_ERROR', payload: null })

            try {
                const response = await itemGroupsService.getById(domain.id, parseInt(groupId, 10))

                if (cancelled) return

                if (response.ok && response.data) {
                    const data = response.data as ItemGroupResponse
                    filtersJsonRef.current = data.filters
                    dispatch({
                        type: 'POPULATE_FORM',
                        payload: { name: data.name },
                    })
                } else {
                    navigateToList()
                }
            } catch {
                if (cancelled) return
                navigateToList()
                dispatch({ type: 'SET_LOADING', payload: false })
            }
        }

        fetchGroup()
        return () => {
            cancelled = true
        }
    }, [isEditMode, domain?.id, groupId, itemGroupsService])

    // ─── Callbacks ───────────────────────────────────────────────────────────
    const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        dispatch({ type: 'SET_NAME', payload: e.target.value })
    }, [])

    const handleRuleCommit = useCallback((value: Record<string, unknown>) => {
        filtersJsonRef.current = value
        dirtyRef.current = true
        // reset submit attempted for inline validation reset
        dispatch({
            type: 'UNSET_SUBMIT_ATTEMPTED',
        })
    }, [])

    const handleValidation = useCallback((result: ValidationResult) => {
        builderValidRef.current = result.valid
    }, [])

    const navigateToList = useCallback(() => {
        appService.routeWithinDomain('/catalogs/item-groups')
    }, [appService])

    // Navigate back to list if the active domain changes (resource belongs to previous domain)
    useActiveDomainChangeEffect((_domain, appSvc) => {
        appSvc.routeWithinDomain('/catalogs/item-groups')
    })

    const validateName = useCallback((): boolean => {
        const trimmed = state.name.trim()
        if (!trimmed) {
            dispatch({ type: 'SET_NAME_ERROR', payload: 'Name is required' })
            return false
        }
        if (trimmed.length > 255) {
            dispatch({ type: 'SET_NAME_ERROR', payload: 'Name must be 255 characters or fewer' })
            return false
        }
        return true
    }, [state.name])

    const handleSubmit = useCallback(async () => {
        dispatch({ type: 'SET_SUBMIT_ATTEMPTED' })
        dispatch({ type: 'SET_SUBMIT_ERROR', payload: null })

        if (!domain) return

        const nameValid = validateName()

        // Validate builder has at least 1 condition
        const filtersJson = filtersJsonRef.current
        const hasConditions =
            filtersJson != null &&
            typeof filtersJson === 'object' &&
            'and' in filtersJson &&
            Array.isArray(filtersJson.and) &&
            filtersJson.and.length > 0

        if (!hasConditions) {
            dispatch({
                type: 'SET_SUBMIT_ERROR',
                payload: 'At least 1 condition is required in the builder.',
            })
        }

        if (!nameValid || !state.builderValid || !hasConditions) {
            return
        }

        dispatch({ type: 'SET_SUBMITTING', payload: true })

        const payload = {
            name: state.name.trim(),
            filters: filtersJsonRef.current ?? {},
        }

        try {
            const response = isEditMode
                ? await itemGroupsService.update(domain.id, parseInt(groupId!, 10), payload)
                : await itemGroupsService.create(domain.id, payload)
            if (response.ok) {
                const displayName = response.data ? response.data.name : undefined
                simpleNotification({
                    method: 'success',
                    description: (
                        <p>
                            Item Group <strong>{displayName}</strong> was successfully{' '}
                            {isEditMode ? 'updated' : 'created'}.
                        </p>
                    ),
                })
                navigateToList()
            } else {
                dispatch({
                    type: 'SET_SUBMIT_ERROR',
                    payload: `Failed to ${isEditMode ? 'update' : 'create'} item group. Please try again.`,
                })
            }
        } catch {
            dispatch({
                type: 'SET_SUBMIT_ERROR',
                payload: 'An unexpected error occurred. Please try again.',
            })
        } finally {
            dispatch({ type: 'SET_SUBMITTING', payload: false })
        }
    }, [validateName, state.name, state.builderValid, isEditMode, domain, groupId, itemGroupsService, navigateToList])

    const handleCancel = useCallback(() => {
        if (dirtyRef.current) {
            antdStaticApi.modal.confirm({
                title: 'Unsaved Changes',
                content: 'You have unsaved changes. Are you sure you want to leave?',
                okText: 'Yes, Leave',
                cancelText: 'Stay',
                onOk: navigateToList,
            })
        } else {
            navigateToList()
        }
    }, [navigateToList])

    // ─── Render ──────────────────────────────────────────────────────────────
    if (!domain || state.loading || fields === null) {
        return <ItemGroupFormSkeleton />
    }

    const builderValue = filtersJsonRef.current ?? EMPTY_BUILDER_VALUE

    return (
        <Flex vertical gap={VERTICAL_WELL_GAP}>
            {state.submitError && (
                <Alert
                    type="error"
                    title={state.submitError}
                    closable={{ onClose: () => dispatch({ type: 'SET_SUBMIT_ERROR', payload: null }) }}
                />
            )}

            <Well title="Details">
                <Flex vertical gap={12}>
                    <div className="item-group-form-page__field">
                        <label htmlFor="item-group-name">Name</label>
                        <Input
                            id="item-group-name"
                            value={state.name}
                            onChange={handleNameChange}
                            placeholder="Enter group name"
                            maxLength={255}
                            status={state.nameError ? 'error' : undefined}
                        />
                        {state.nameError && <div className="item-group-form-page__field-error">{state.nameError}</div>}
                    </div>
                </Flex>
            </Well>

            <PQueryBuilderProvider
                value={builderValue}
                fields={fields}
                timezone={timezone}
                constraints={{ allowedGroupTypes: ['or'] }}
                labels={{
                    addGroup: 'Add group',
                    addCondition: 'Add condition',
                }}
                onRuleCommit={handleRuleCommit}
                onValidation={handleValidation}
            >
                <Well
                    className="item-group-form-page__builder"
                    title="Builder"
                    actions={<CountPreview domainId={domain.id} />}
                    onSubmit={handleSubmit}
                    submitText="Save"
                    onCancel={handleCancel}
                    cancelText="Cancel"
                    footerMode="ghost"
                >
                    <PQueryBuilder showErrors={state.submitAttempted && !state.builderValid} />
                </Well>
            </PQueryBuilderProvider>
        </Flex>
    )
}
